import type { ReactNode, SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  /** Icon size as a CSS length. Defaults to `'1em'` so the glyph
   *  scales with the parent's `font-size` — drop the icon next to
   *  text and it matches automatically, no explicit sizing needed.
   *  Pass a number (px) or any CSS length string to override. */
  size?: number | string;
  children?: ReactNode;
}

/**
 * Base SVG wrapper for the icon set. Defaults:
 *   • `stroke = 'currentColor'` → colour inherits from text colour, so
 *     the same icon adapts to whichever theme slot it's nested under
 *     (Button text, link colour, etc.).
 *   • `width / height = '1em'` → size follows the parent's font-size.
 *   • `viewBox = '0 0 24 24'` → 24-unit grid used by every icon below.
 *   • `aria-hidden` by default — decorative use is the common case.
 *
 * Each concrete icon (`ChevronLeft`, `Plus`, …) passes only its path
 * data as children; everything else can be overridden via props.
 */
export function Icon({
  size = '1em',
  width,
  height,
  fill = 'none',
  stroke = 'currentColor',
  strokeWidth = 2,
  strokeLinecap = 'round',
  strokeLinejoin = 'round',
  viewBox = '0 0 24 24',
  'aria-hidden': ariaHidden = true,
  children,
  ...rest
}: IconProps) {
  return (
    <svg
      width={width ?? size}
      height={height ?? size}
      viewBox={viewBox}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap={strokeLinecap}
      strokeLinejoin={strokeLinejoin}
      aria-hidden={ariaHidden}
      {...rest}
    >
      {children}
    </svg>
  );
}
